import React, { useEffect, useMemo, useState } from 'react';
import { Form, Input, InputNumber, DatePicker, Select, message, Card, Space, Typography } from 'antd';
import { fetchSqlData, insertData } from '../../../services/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AdmFuelPump, AdmVehicle, Personnel } from '../../../types';
import { syncVehicleUsageCounters } from '../vehicles/vehicleStatusSync';
import { useTranslation } from 'react-i18next';
import { useGetPersonnel } from '../../../hooks/genericHooks';
import Button from '../../../components/common/Button';

const VehicleFuelEntryForm: React.FC = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage === 'tr' ? 'tr-TR' : 'en-US';
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const selectedVehicleId = Form.useWatch('vehicle_id', form);
  const selectedSourceType = Form.useWatch('source_type', form);
  const selectedFuelType = Form.useWatch('fuel_type', form);
  const selectedSourcePumpId = Form.useWatch('source_pump_id', form);
  const selectedSourceVehicleId = Form.useWatch('source_vehicle_id', form);

  const { data: vehicles = [] } = useQuery<AdmVehicle[], Error>({
    queryKey: ['machineryVehicles'],
    queryFn: () => fetchSqlData('adm_vehicles'),
    staleTime: 5 * 60_000,
  });
  const { data: personnelResp } = useGetPersonnel();
  const personnel = useMemo<Personnel[]>(() => {
    const arr = Array.isArray(personnelResp) ? personnelResp : (personnelResp as any)?.data || [];
    return arr as Personnel[];
  }, [personnelResp]);
  const { data: fuelPumps = [] } = useQuery<AdmFuelPump[], Error>({
    queryKey: ['fuelPumps'],
    queryFn: () => fetchSqlData('adm_fuel_pumps'),
    staleTime: 5 * 60_000,
  });

  const selectedVehicle = useMemo(
    () => vehicles.find(vehicle => Number(vehicle.id) === Number(selectedVehicleId)),
    [vehicles, selectedVehicleId]
  );

  const vehicleOptions = useMemo(
    () =>
      vehicles.map(vehicle => ({
        value: vehicle.id,
        label: `${vehicle.license_plate || '-'} ${vehicle.make || ''} ${vehicle.model || ''}`.trim(),
      })),
    [vehicles]
  );

  const personnelOptions = useMemo(
    () =>
      personnel.map(person => ({
        value: person.id,
        label: `${person.first_name || ''} ${person.last_name || ''}`.trim(),
      })),
    [personnel]
  );

  const activeFuelPumps = useMemo(
    () => fuelPumps.filter(pump => pump.status === 'active'),
    [fuelPumps]
  );

  const tankerVehicles = useMemo(
    () =>
      vehicles.filter(vehicle =>
        vehicle.machine_type === 'Fuel Tanker' &&
        Number(vehicle.id) !== Number(selectedVehicleId)
      ),
    [vehicles, selectedVehicleId]
  );

  const fuelTypeOptions = useMemo(
    () => ['Diesel', 'Gasoline', 'LPG', 'Electric', 'Hybrid'].map(value => ({ value, label: value })),
    []
  );

  const sourceTypeOptions = useMemo(
    () => [
      { value: 'fixed_pump', label: t('machinery.fuel.entry.sourceTypeFixedPump') },
      { value: 'fuel_tanker', label: t('machinery.fuel.entry.sourceTypeFuelTanker') },
      { value: 'external', label: t('machinery.fuel.entry.sourceTypeExternal') },
    ],
    [t]
  );

  const selectedPump = useMemo(
    () => activeFuelPumps.find(pump => Number(pump.id) === Number(selectedSourcePumpId)),
    [activeFuelPumps, selectedSourcePumpId]
  );

  const selectedTanker = useMemo(
    () => tankerVehicles.find(vehicle => Number(vehicle.id) === Number(selectedSourceVehicleId)),
    [selectedSourceVehicleId, tankerVehicles]
  );

  const pumpOptions = useMemo(
    () =>
      activeFuelPumps
        .filter(pump => !selectedFuelType || pump.fuel_type === selectedFuelType)
        .map(pump => ({
          value: pump.id,
          label: [pump.name, pump.location].filter(Boolean).join(' - '),
        })),
    [activeFuelPumps, selectedFuelType]
  );

  const tankerOptions = useMemo(
    () =>
      tankerVehicles
        .filter(vehicle => !selectedFuelType || vehicle.fuel_type === selectedFuelType)
        .map(vehicle => ({
          value: vehicle.id,
          label: `${vehicle.license_plate || '-'} ${vehicle.make || ''} ${vehicle.model || ''}`.trim(),
        })),
    [selectedFuelType, tankerVehicles]
  );

  useEffect(() => {
    form.setFieldsValue({
      fuel_type: selectedVehicle?.fuel_type || form.getFieldValue('fuel_type'),
    });
  }, [form, selectedVehicle]);

  useEffect(() => {
    if (selectedSourceType === 'fixed_pump') {
      form.setFieldsValue({ source_vehicle_id: undefined, source_name: undefined });
      return;
    }

    if (selectedSourceType === 'fuel_tanker') {
      form.setFieldsValue({ source_pump_id: undefined, source_name: undefined });
      return;
    }

    if (selectedSourceType === 'external') {
      form.setFieldsValue({ source_pump_id: undefined, source_vehicle_id: undefined });
    }
  }, [form, selectedSourceType]);

  useEffect(() => {
    if (selectedSourceType === 'fixed_pump' && selectedSourcePumpId && !selectedPump) {
      form.setFieldsValue({ source_pump_id: undefined });
    }

    if (selectedSourceType === 'fuel_tanker' && selectedSourceVehicleId && !selectedTanker) {
      form.setFieldsValue({ source_vehicle_id: undefined });
    }
  }, [form, selectedPump, selectedSourcePumpId, selectedSourceType, selectedSourceVehicleId, selectedTanker]);

  const invalidateFuelQueries = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['vehicleFuelEntries'] }),
      queryClient.invalidateQueries({ queryKey: ['machineryFuelEntries'] }),
      queryClient.invalidateQueries({ queryKey: ['machineryVehicles'] }),
      queryClient.invalidateQueries({ queryKey: ['machineryVehicleDetail'] }),
      queryClient.invalidateQueries({ queryKey: ['vehicleDetail'] }),
      queryClient.invalidateQueries({ queryKey: ['fuelPumps'] }),
    ]);
  };

  const handleFinish = async (values: any) => {
    setLoading(true);
    try {
      const derivedSourceName =
        values.source_type === 'fixed_pump'
          ? selectedPump?.name || null
          : values.source_type === 'fuel_tanker'
            ? `${selectedTanker?.license_plate || '-'} ${selectedTanker?.make || ''} ${selectedTanker?.model || ''}`.trim()
            : values.source_name?.trim() || null;

      const payload = {
        vehicle_id: Number(values.vehicle_id),
        personnel_id: values.personnel_id ? Number(values.personnel_id) : null,
        entry_date: values.entry_date.format('YYYY-MM-DD'),
        odometer_reading: values.odometer_reading ?? null,
        hours_counter_reading: values.hours_counter_reading ?? null,
        liters: values.liters,
        fuel_station: derivedSourceName,
        company: values.company?.trim() || null,
        source_type: values.source_type,
        source_pump_id: values.source_type === 'fixed_pump' ? Number(values.source_pump_id) : null,
        source_vehicle_id: values.source_type === 'fuel_tanker' ? Number(values.source_vehicle_id) : null,
        source_name: values.source_type === 'external' ? values.source_name?.trim() || null : derivedSourceName,
        fuel_type: values.fuel_type,
        location: values.location?.trim() || null,
        reference_no: values.reference_no?.trim() || null,
        note: values.note?.trim() || null,
      };
      await insertData('adm_vehicle_fuel_entries', payload);
      await syncVehicleUsageCounters(payload.vehicle_id, {
        odometerReading: payload.odometer_reading,
        hoursCounterReading: payload.hours_counter_reading,
      });
      await invalidateFuelQueries();
      message.success(t('machinery.fuel.entry.addSuccess'));
      form.resetFields();
    } catch (e: any) {
      message.error(t('machinery.fuel.entry.addFailed', { message: e?.response?.data?.error || e.message }));
    }
    setLoading(false);
  };

  return (
    <Card title={t('machinery.fuel.entry.title')} style={{ maxWidth: 720, margin: '0 auto' }}>
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item
          name="vehicle_id"
          label={t('machinery.fuel.entry.vehicle')}
          rules={[{ required: true, message: t('machinery.fuel.entry.vehicleRequired') }]}
        >
          <Select showSearch placeholder={t('machinery.fuel.entry.vehiclePlaceholder')} options={vehicleOptions} optionFilterProp="label" />
        </Form.Item>

        {selectedVehicle ? (
          <Card size="small" style={{ marginBottom: 16 }}>
            <Space size={24} wrap>
              <Typography.Text type="secondary">
                {t('machinery.fuel.entry.currentKilometer')}: <strong>{selectedVehicle.kilometer?.toLocaleString(locale) || '-'}</strong>
              </Typography.Text>
              <Typography.Text type="secondary">
                {t('machinery.fuel.entry.currentHours')}: <strong>{selectedVehicle.hours_counter?.toLocaleString(locale) || '-'}</strong>
              </Typography.Text>
              <Typography.Text type="secondary">
                {t('machinery.fuel.entry.vehicleFuelType')}: <strong>{selectedVehicle.fuel_type || '-'}</strong>
              </Typography.Text>
            </Space>
          </Card>
        ) : null}

        <Form.Item
          name="fuel_type"
          label={t('machinery.fuel.entry.fuelType')}
          rules={[{ required: true, message: t('machinery.fuel.entry.fuelTypeRequired') }]}
        >
          <Select
            placeholder={t('machinery.fuel.entry.fuelTypePlaceholder')}
            options={fuelTypeOptions}
            disabled={!!selectedVehicle?.fuel_type}
          />
        </Form.Item>

        <Form.Item
          name="source_type"
          label={t('machinery.fuel.entry.sourceType')}
          rules={[{ required: true, message: t('machinery.fuel.entry.sourceTypeRequired') }]}
        >
          <Select placeholder={t('machinery.fuel.entry.sourceTypePlaceholder')} options={sourceTypeOptions} />
        </Form.Item>

        {selectedSourceType === 'fixed_pump' ? (
          <Form.Item
            name="source_pump_id"
            label={t('machinery.fuel.entry.sourcePump')}
            rules={[{ required: true, message: t('machinery.fuel.entry.sourcePumpRequired') }]}
          >
            <Select
              showSearch
              placeholder={t('machinery.fuel.entry.sourcePumpPlaceholder')}
              options={pumpOptions}
              optionFilterProp="label"
            />
          </Form.Item>
        ) : null}

        {selectedSourceType === 'fuel_tanker' ? (
          <Form.Item
            name="source_vehicle_id"
            label={t('machinery.fuel.entry.sourceTanker')}
            rules={[{ required: true, message: t('machinery.fuel.entry.sourceTankerRequired') }]}
          >
            <Select
              showSearch
              placeholder={t('machinery.fuel.entry.sourceTankerPlaceholder')}
              options={tankerOptions}
              optionFilterProp="label"
            />
          </Form.Item>
        ) : null}

        {selectedSourceType === 'external' ? (
          <Form.Item
            name="source_name"
            label={t('machinery.fuel.entry.sourceName')}
            rules={[{ required: true, message: t('machinery.fuel.entry.sourceNameRequired') }]}
          >
            <Input placeholder={t('machinery.fuel.entry.sourceNamePlaceholder')} />
          </Form.Item>
        ) : null}

        <Form.Item
          name="personnel_id"
          label={t('machinery.fuel.entry.personnel')}
          rules={[{ required: true, message: t('machinery.fuel.entry.personnelRequired') }]}
        >
          <Select showSearch placeholder={t('machinery.fuel.entry.personnelPlaceholder')} options={personnelOptions} optionFilterProp="label" />
        </Form.Item>
        <Form.Item
          name="entry_date"
          label={t('machinery.fuel.entry.date')}
          rules={[{ required: true, message: t('machinery.fuel.entry.dateRequired') }]}
        >
          <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
        </Form.Item>
        <Form.Item name="odometer_reading" label={t('machinery.fuel.entry.odometer')}>
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="hours_counter_reading" label={t('machinery.fuel.entry.hourCounter')}>
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name="liters"
          label={t('machinery.fuel.entry.liters')}
          rules={[{ required: true, message: t('machinery.fuel.entry.litersRequired') }]}
        >
          <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="location" label={t('machinery.fuel.entry.location')}>
          <Input placeholder={t('machinery.fuel.entry.locationPlaceholder')} />
        </Form.Item>
        <Form.Item name="reference_no" label={t('machinery.fuel.entry.referenceNo')}>
          <Input placeholder={t('machinery.fuel.entry.referenceNoPlaceholder')} />
        </Form.Item>
        <Form.Item name="company" label={t('machinery.fuel.entry.company')}>
          <Input placeholder={t('machinery.fuel.entry.companyPlaceholder')} />
        </Form.Item>
        <Form.Item name="note" label={t('machinery.fuel.entry.note')}>
          <Input.TextArea rows={3} placeholder={t('machinery.fuel.entry.notePlaceholder')} />
        </Form.Item>
        <Form.Item style={{ marginBottom: 0 }}>
          <Button action="add" type="primary" htmlType="submit" loading={loading}>
            {t('machinery.fuel.entry.save')}
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default VehicleFuelEntryForm; 